import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const FAQS = [
  {
    q: "Do you provide free collision repair estimates in Carrollton?",
    a: "Yes, Westway Collision Center provides comprehensive free estimates. Submit photos through our online estimate form or visit our facility at 1420 Westway Cir in Carrollton for an in-person diagnostic evaluation — zero obligation, total transparency.",
  },
  {
    q: "Can I choose my own auto body shop for an insurance claim in Texas?",
    a: "Absolutely. Texas state law guarantees your legal right to choose any certified repair facility for your vehicle restoration. Westway Collision Center works directly with all major insurance providers, managing supplements and documentation directly so you do not have to.",
  },
  {
    q: "What does I-CAR Gold Class certification mean for my car repair?",
    a: "I-CAR Gold Class is the highest standard tier recognized in the collision industry. It ensures our certified technicians undergo continuous, rigorous training to properly navigate advanced vehicle materials, computerized structural alignment, and complex electronic ADAS safety systems.",
  },
  {
    q: "Do you handle the insurance claim process directly?",
    a: "Yes. We manage the entire claims process with all major carriers — including supplement approvals and adjuster communications — so your only job is dropping off the keys.",
  },
];

export const FAQ = () => {
  const [open, setOpen] = useState(0);

  return (
    <section id="faq" data-testid="faq-section" className="relative py-24 lg:py-32">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="text-center flex flex-col items-center">
          <Eyebrow>Straight Answers</Eyebrow>
          <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white text-balance">
            Questions, <span className="accent-text">Answered Precisely</span>
          </h2>
        </Reveal>

        <div className="mt-12 flex flex-col gap-4">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <Reveal key={f.q} delay={i * 0.06}>
                <div
                  data-testid={`faq-accordion-item-${i}`}
                  className={`rounded-2xl border transition-colors duration-500 ${
                    isOpen ? "border-accent/30 bg-surface" : "border-white/[0.08] bg-surface/50 hover:border-white/20"
                  }`}
                >
                  <h3>
                    <button
                      data-testid={`faq-question-${i}`}
                      onClick={() => setOpen(isOpen ? -1 : i)}
                      aria-expanded={isOpen}
                      className="flex w-full items-center justify-between gap-6 px-6 sm:px-8 py-6 text-left"
                    >
                      <span className={`font-display text-base sm:text-lg font-semibold transition-colors ${isOpen ? "text-accent" : "text-slate-100"}`}>
                        {f.q}
                      </span>
                      <motion.span
                        animate={{ rotate: isOpen ? 45 : 0 }}
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                        className={`shrink-0 rounded-full border p-2 ${isOpen ? "border-accent/50 text-accent" : "border-white/15 text-mute"}`}
                      >
                        <Plus size={16} />
                      </motion.span>
                    </button>
                  </h3>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden"
                      >
                        <p data-testid={`faq-answer-${i}`} className="px-6 sm:px-8 pb-7 text-sm sm:text-base text-slate-400 leading-relaxed max-w-3xl">
                          {f.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal className="mt-10 text-center">
          <Link
            to="/faq"
            data-testid="home-faq-more-link"
            className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase text-accent hover:text-accentdeep transition-colors"
          >
            Read the full FAQ <ArrowUpRight size={14} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};
